/**
 * Frontend-Pendant zu `src/server/bookingHistory/bookingHistory.helpers.ts`.
 *
 * Backend haengt an Booking-History-Descriptions, die aus dem Course-Edit-
 * Pfad (`updateCourseAndBookings -> editMultipleBookings`) stammen, einen
 * statischen Suffix " (durch Kursanpassung)" an. Das Frontend nutzt diesen
 * Suffix als Trigger-Marker, um:
 *
 * 1. einen separaten farbigen Tag in der Timeline zu rendern,
 *    statt den Suffix als Plain-Text Teil der Beschreibung anzuzeigen,
 * 2. im Detail-Modal einen Hinweis und Quicklink in die Kurshistorie zu
 *    bieten.
 *
 * SYNC: Wenn der Suffix-String im Backend geaendert wird
 * (siehe `bookingHistory.helpers.ts:10`), MUSS er hier mit aktualisiert
 * werden, sonst funktioniert die Erkennung im Frontend nicht mehr.
 */
export const COURSE_EDIT_SUFFIX = " (durch Kursanpassung)";

/**
 * Ergebnis der {@link parseDescription}-Funktion.
 */
export type ParsedDescription = {
    /** Beschreibung ohne Trigger-Suffix (idempotent). */
    cleanDescription: string;
    /** True, wenn der Suffix erkannt wurde - die Aenderung stammt aus einem Course-Edit. */
    isCourseEdit: boolean;
};

/**
 * Parst eine Booking-History-Description und trennt einen ggf. vorhandenen
 * Course-Edit-Suffix ab.
 *
 * Idempotent gegen mehrfaches Auftreten des Suffix (entfernt jeweils nur den
 * letzten Vorkommen, wiederholt bis kein Suffix mehr da ist - sollte in der
 * Praxis nie vorkommen, da der Backend-Helper ebenfalls idempotent
 * angehaengt). Sicher gegen null/undefined/Leerstring.
 *
 * @param description Rohbeschreibung aus dem Booking-History-Eintrag
 * @returns Aufgespaltete Beschreibung + Trigger-Flag
 */
export const parseDescription = (description: string | null | undefined): ParsedDescription => {
    if (description === null || description === undefined || description === "") {
        return {cleanDescription: description ?? "", isCourseEdit: false};
    }

    let clean = description;
    let found = false;

    // Idempotente Mehrfach-Entfernung am Ende (Defensive: falls der Suffix
    // versehentlich mehrfach angehaengt wurde).
    while (clean.endsWith(COURSE_EDIT_SUFFIX)) {
        clean = clean.substring(0, clean.length - COURSE_EDIT_SUFFIX.length);
        found = true;
    }

    return {cleanDescription: clean, isCourseEdit: found};
};
